import { Chip } from "@mui/material";
import { useController } from "react-hook-form";
import * as yup from "yup";

import {
  FormControlLabel,
  SliderWithTextField,
  Switch,
  Tooltip,
  Typography,
} from "@/components/ui-v2";

import { FieldComponentProps } from "../../types";
import {
  getMaxErrorMessage,
  getMinErrorMessage,
} from "../../validationMessages";
import { FIELD_NAMES } from "../constants";
import { FieldDef, FieldDefOverrides } from "../FieldDefs";

export type AudioDurationFieldValue = {
  value: number;
  isAuto: boolean;
};

export interface AudioDurationComponentProps {
  min?: number;
  max?: number;
  step?: number;
}

export const MIN_AUDIO_DURATION = 1;
export const MAX_AUDIO_DURATION = 22;
export const DEFAULT_AUDIO_DURATION_VALUE = 5;
export const DEFAULT_AUTO_DURATION = false;

const DEFAULT_VALUE: AudioDurationFieldValue = {
  value: DEFAULT_AUDIO_DURATION_VALUE,
  isAuto: DEFAULT_AUTO_DURATION,
};

const valueFieldName = `${FIELD_NAMES.audioDuration}.value`;
const isAutoFieldName = `${FIELD_NAMES.audioDuration}.isAuto`;

const AudioDuration = (
  props: FieldComponentProps<AudioDurationFieldValue> &
    AudioDurationComponentProps,
) => {
  const { controller: _controller, disabled = false, min, max, step } = props;

  const { field: valueField, fieldState: valueFieldState } = useController({
    name: valueFieldName,
  });

  const { field: isAutoField } = useController({
    name: isAutoFieldName,
  });

  return (
    <SliderWithTextField
      disabled={disabled}
      field={valueField}
      error={valueFieldState.error}
      label="Duration"
      tooltip="The duration of the sound effect, in seconds."
      step={step}
      min={min}
      max={max}
      adornment="s"
      labelEnd={
        <FormControlLabel
          // To have better looking alignment
          sx={{ pr: "11px" }}
          checked={isAutoField.value === true}
          onChange={(_, checked) => isAutoField.onChange(checked)}
          control={<Switch size="small" />}
          labelPlacement="start"
          disabled={disabled}
          label={
            <Tooltip title="Enable the model to choose the best duration for the submitted prompt.">
              <Typography variant="small" color="secondary.1" sx={{ mr: 1 }}>
                Auto
              </Typography>
            </Tooltip>
          }
        />
      }
      textField={
        isAutoField.value === true ? (
          <Chip color="primary" label="Auto" size="small" />
        ) : null
      }
      sliderProps={{
        disabled: isAutoField.value,
      }}
    />
  );
};

export const createAudioDurationFieldDef = (
  overrides?: FieldDefOverrides<"audioDuration">,
): FieldDef => ({
  name: FIELD_NAMES.audioDuration,
  Component: AudioDuration,
  validationSchema: yup.object().shape({
    value: yup
      .number()
      .min(
        MIN_AUDIO_DURATION,
        getMinErrorMessage("Duration", MIN_AUDIO_DURATION),
      )
      .max(
        MAX_AUDIO_DURATION,
        getMaxErrorMessage("Duration", MAX_AUDIO_DURATION),
      ),
    isAuto: yup.boolean(),
  }),
  defaultValue: DEFAULT_VALUE,
  ...overrides,
  emptyValue: DEFAULT_VALUE,
  componentProps: {
    min: MIN_AUDIO_DURATION,
    max: MAX_AUDIO_DURATION,
    step: 0.1,
    ...overrides?.componentProps,
  },
});
